import { useTranslation } from 'react-i18next';
import useCustomizationSummary from '../../CustomizationSummary/useCustomizationSummary';
import useWarranties from '../../WarrantyOptions/useWarranties';
import { WarrantyTypes } from '../../../core/types';
const useSelectedItems = () => {
    const { t } = useTranslation();
    const { accessories, wheels, charging, hasChargingAccessories, protections, exteriorColorHasPriceMarkup, exteriorColorPriceText, exteriorColorPaymentText, interiorColorHasPriceMarkup, interiorColorPaymentText, interiorColorPriceText, vehicle, } = useCustomizationSummary();
    const { selectedWarranty, standardWarranty } = useWarranties();
    const getWarrantyDictionaryValue = (type) => {
        var _a, _b, _c;
        switch (type) {
            case WarrantyTypes.Extended:
                return `${t('Pages.Bap.extendedLabel')} | ${(_a = selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.name) === null || _a === void 0 ? void 0 : _a.replace('* ', '')}`;
            case WarrantyTypes.Flexible:
                return `${t('Pages.Bap.flexibleLabel')} | ${(_b = selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.name) === null || _b === void 0 ? void 0 : _b.replace('* ', '')}`;
            default:
                return `${t('Pages.Bap.standardLabel')} | ${(_c = standardWarranty === null || standardWarranty === void 0 ? void 0 : standardWarranty.name) === null || _c === void 0 ? void 0 : _c.replace('* ', '')}`;
        }
    };
    const createItem = ({ id, categoryTitle, showIncludedBadge, title, priceText, paymentText, imageSrc, colorHexValue, termLabel, isWarranty, }) => ({
        id,
        categoryTitle,
        showIncludedBadge,
        title,
        priceText,
        paymentText,
        imageSrc,
        colorHexValue,
        termLabel,
        isWarranty,
    });
    const selectedWheels = (wheels === null || wheels === void 0 ? void 0 : wheels.map(wheel => createItem({
        id: 'wheel',
        categoryTitle: t('Pages.Bap.wheelsLabel'),
        showIncludedBadge: wheel.isIncluded,
        title: wheel.name,
        priceText: wheel.priceText,
        paymentText: wheel.paymentText,
        imageSrc: wheel.imageSrc || wheel.imageFallbackSrc,
    }))) || [];
    const selectedCharging = [];
    if (hasChargingAccessories) {
        if (charging === null || charging === void 0 ? void 0 : charging.length) {
            selectedCharging.push(...charging.map(charger => createItem({
                id: 'charging',
                categoryTitle: t('Pages.Bap.chargingLabel'),
                showIncludedBadge: charger.isIncluded,
                title: charger.name,
                priceText: charger.priceText,
                paymentText: charger.paymentText,
                imageSrc: charger.imageSrc || charger.imageFallbackSrc,
            })));
        }
        else {
            selectedCharging.push({
                id: 'charging',
                categoryTitle: t('Pages.Bap.chargingLabel'),
                noSelectedItemsLabel: t('Pages.Bap.noChargingSummaryPdfText'),
            });
        }
    }
    const selectedAccessories = (accessories === null || accessories === void 0 ? void 0 : accessories.length)
        ? accessories.map(accessory => createItem({
            id: 'accessories',
            categoryTitle: t('Pages.Bap.accessoriesLabel'),
            showIncludedBadge: accessory.isIncluded,
            title: accessory.name,
            priceText: accessory.priceText,
            paymentText: accessory.paymentText,
            imageSrc: accessory.imageSrc || accessory.imageFallbackSrc,
        }))
        : [
            {
                id: 'accessories',
                categoryTitle: t('Pages.Bap.accessoriesLabel'),
                noSelectedItemsLabel: t('Pages.Bap.noAccessorySummaryPdfText'),
            },
        ];
    const selectedProtections = (protections === null || protections === void 0 ? void 0 : protections.length)
        ? protections.map(protection => createItem({
            id: 'protections',
            categoryTitle: t('Pages.Bap.protectionLabel'),
            showIncludedBadge: !protection.price,
            title: protection.name,
            priceText: protection.priceText,
            paymentText: protection.paymentText,
            imageSrc: protection.imageSrc,
            termLabel: protection.paymentTerm
                ? t('Pages.Bap.termMonthsLabel', { term: protection.paymentTerm })
                : undefined,
        }))
        : [
            {
                id: 'protections',
                categoryTitle: t('Pages.Bap.protectionLabel'),
                noSelectedItemsLabel: t('Pages.Bap.noProtectionSummaryPdfText'),
            },
        ];
    return [
        createItem({
            id: 'exterior',
            categoryTitle: t('Shared.Common.exteriorColorLabel'),
            showIncludedBadge: !exteriorColorHasPriceMarkup,
            title: vehicle.exteriorColourName,
            priceText: exteriorColorPriceText,
            paymentText: exteriorColorPaymentText,
            colorHexValue: vehicle === null || vehicle === void 0 ? void 0 : vehicle.exteriorColourHexValue,
        }),
        ...selectedWheels,
        createItem({
            id: 'interior',
            categoryTitle: t('Shared.Common.interiorColorLabel'),
            showIncludedBadge: !interiorColorHasPriceMarkup,
            title: vehicle.interiorColourName,
            priceText: interiorColorPriceText,
            paymentText: interiorColorPaymentText,
            imageSrc: vehicle.interiorColourPreviewImageSrc,
        }),
        ...selectedCharging,
        ...selectedAccessories,
        ...selectedProtections,
        createItem({
            id: 'warranty',
            categoryTitle: t('Pages.Bap.warrantyLabel'),
            priceText: selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.priceText,
            paymentText: selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.paymentText,
            title: getWarrantyDictionaryValue(selectedWarranty === null || selectedWarranty === void 0 ? void 0 : selectedWarranty.bapType),
            isWarranty: true,
        }),
    ];
};
export default useSelectedItems;
